import React, { useEffect, useState } from 'react';
import { Trash2, Copy, Clipboard, X } from 'lucide-react';
import { GetNodeOptions_getNodeOptions_actionOptions, GetNodeOptions_getNodeOptions_conditionOptions } from '@frontend/app/queries/types/GetNodeOptions';
import { IAutomationVariable } from '@frontend/app/types/automations/condition/AutomationVariable';
import { AutomationActionNodeSubType } from '@frontend/app/types/globalTypes';
import { Card, CardContent, CardHeader, CardTitle } from '@/shadcn/components/ui/card';
import { Button } from '@/shadcn/components/ui/button';
import { Label } from '@/shadcn/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shadcn/components/ui/select';
import { Badge } from '@/shadcn/components/ui/badge';
import { AutomationNodeV2 } from '../types';
import { ConditionFormV2 } from './ConditionFormV2';
import { SendEmailActionForm, GenericActionForm, UpdateMemberDataActionForm } from './ActionTypes';
import { InlineTimeSelector } from '../NodeComponents/InlineTimeSelector';

const getDelayStateFromDuration = (durationMs: number) => {
  const totalHours = durationMs / 3600000; // Convert ms to hours
  const totalDays = totalHours / 24; // Convert hours to days

  // If >= 1 day, display in days (with decimals)
  const isInDays = totalDays >= 1;

  if (isInDays) {
    const amount = totalDays % 1 === 0 ? Math.floor(totalDays) : Number(totalDays.toFixed(1));
    return { amount, unit: 'days' };
  } else {
    const amount = totalHours % 1 === 0 ? Math.floor(totalHours) : Number(totalHours.toFixed(1));
    return { amount, unit: 'hours' };
  }
};

interface PropertyPanelProps {
  selectedNode: AutomationNodeV2 | null;
  onUpdateNode: (nodeId: string, updates: Partial<AutomationNodeV2>) => void;
  onDeleteNode: (nodeId: string) => void;
  onCopyNode: (nodeId: string) => void;
  canPaste: boolean;
  onPasteNode: (parentId: string) => void;
  onClose?: () => void;
  actionOptions?: GetNodeOptions_getNodeOptions_actionOptions[];
  conditionOptions?: GetNodeOptions_getNodeOptions_conditionOptions[];
  automationVariables?: IAutomationVariable[];
  onUpdateMessageTemplate?: (updatedTemplate: any) => void;
  resetMessageTemplate?: boolean;
  isRoot?: boolean; // Flag to indicate if the selected node is a root node
}

export const PropertyPanel: React.FC<PropertyPanelProps> = ({
  selectedNode,
  onUpdateNode,
  onDeleteNode,
  onCopyNode,
  canPaste,
  onPasteNode,
  onClose,
  actionOptions = [],
  conditionOptions = [],
  automationVariables = [],
  onUpdateMessageTemplate,
  resetMessageTemplate = false,
  isRoot = false,
}) => {
  // State for delay node
  const [tempDelayAmount, setTempDelayAmount] = useState(() => {
    if (selectedNode?.type === 'DELAY') {
      return getDelayStateFromDuration(selectedNode.metadata.durationMs || 3600000).amount;
    }
    return 1;
  });

  const [tempDelayUnit, setTempDelayUnit] = useState(() => {
    if (selectedNode?.type === 'DELAY') {
      return getDelayStateFromDuration(selectedNode.metadata.durationMs || 3600000).unit;
    }
    return 'hours';
  });

  // Update delay state when selectedNode changes
  useEffect(() => {
    if (selectedNode?.type === 'DELAY') {
      const { amount, unit } = getDelayStateFromDuration(selectedNode.metadata.durationMs || 3600000);
      setTempDelayAmount(amount);
      setTempDelayUnit(unit);
    }
  }, [selectedNode?.id, selectedNode?.metadata.durationMs, selectedNode?.type]);

  // Initialize action node metadata on selection to prevent infinite loops
  useEffect(() => {
    if (selectedNode && selectedNode.type === 'ACTION') {
      const missingFields: { resourceId?: number, fromPreviousSender?: boolean, templateId?: number } = {};
      if (!selectedNode.metadata.resourceId) {
        missingFields.resourceId = 1; // Default resource ID
      }
      if (selectedNode.metadata.fromPreviousSender === undefined) {
        missingFields.fromPreviousSender = false;
      }
      if (selectedNode.subType === AutomationActionNodeSubType.SEND_EMAIL && !selectedNode.metadata.templateId) {
        missingFields.templateId = 1; // Default template ID for email
      }

      // Only update if there are missing fields
      if (Object.keys(missingFields).length > 0) {
        onUpdateNode(selectedNode.id, {
          metadata: {
            ...selectedNode.metadata,
            ...missingFields,
          },
        });
      }
    }
  }, [selectedNode, onUpdateNode]);
  if (!selectedNode) {
    return (
      <Card className="h-full rounded-none border-r-0 border-t-0 border-b-0">
        <CardHeader>
          <CardTitle className="text-base">Properties</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-grey-4">
            <div className="text-sm">Select a node to view its properties</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const handleMetadataUpdate = (key: string, value: unknown) => {
    onUpdateNode(selectedNode.id, {
      metadata: {
        ...selectedNode.metadata,
        [key]: value,
      },
    });
  };

  const handleDelete = () => {
    onDeleteNode(selectedNode.id);
  };

  const handleCopy = () => {
    onCopyNode(selectedNode.id);
  };

  const handlePaste = () => {
    onPasteNode(selectedNode.id);
  };

  const renderNodeSpecificProperties = () => {
    switch (selectedNode.type) {
      case 'DELAY': {
        const originalDurationMs = selectedNode.metadata.durationMs || 3600000; // Default 1 hour

        const convertToMs = (amount: number, unit: string): number => {
          const multipliers = {
            hours: 3600000,
            days: 86400000,
          };
          // Round to integer to satisfy API validation (durationMs must be integer)
          return Math.round(amount * (multipliers[unit as keyof typeof multipliers] || 3600000));
        };

        const handleDone = () => {
          handleMetadataUpdate('durationMs', convertToMs(tempDelayAmount, tempDelayUnit));
          onClose?.();
        };

        const handleCancel = () => {
          // Reset to original values
          const { amount, unit } = getDelayStateFromDuration(originalDurationMs);
          setTempDelayAmount(amount);
          setTempDelayUnit(unit);
          onClose?.();
        };

        return (
          <div className="space-y-4">
            <InlineTimeSelector
              value={tempDelayAmount}
              unit={tempDelayUnit}
              onChange={(amount, unit) => {
                // Only update temporary state, not the actual metadata
                setTempDelayAmount(amount);
                setTempDelayUnit(unit);
              }}
            />
            <div className="flex justify-end gap-2 pt-4 border-t border-grey-2">
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button onClick={handleDone}>
                Done
              </Button>
            </div>
          </div>
        );
      }

      case 'CONDITION': {
        return (
          <ConditionFormV2
            selectedNode={selectedNode}
            onUpdateNode={onUpdateNode}
            onClose={onClose || (() => {})}
            automationVariables={automationVariables}
            conditionOptions={conditionOptions}
          />
        );
      }

      case 'ACTION': {
        // Action Type Selector
        const actionTypeSelector = (
          <div className="space-y-2 mb-4 px-1">
            <Label>Action Type</Label>
            <Select
              value={selectedNode.subType || ''}
              onValueChange={(value) => {
                const getCleanMetadataForActionType = (actionType: string) => {
                  const baseMetadata = {
                    resourceId: selectedNode.metadata.resourceId || 1,
                    fromPreviousSender: selectedNode.metadata.fromPreviousSender ?? false,
                  };

                  let cleanMetadata: any;
                  switch (actionType) {
                    case 'SEND_EMAIL':
                      cleanMetadata = {
                        ...baseMetadata,
                        subType: 'SEND_EMAIL',
                        templateId: selectedNode.metadata.templateId || 1,
                      };
                      break;
                    case 'HANDLE_PFA_BOOST':
                      cleanMetadata = {
                        ...baseMetadata,
                        subType: 'HANDLE_PFA_BOOST',
                        boostId: selectedNode.metadata.boostId,
                        brandCatalogId: selectedNode.metadata.brandCatalogId,
                      };
                      break;
                    case 'UPDATE_MEMBER_DATA':
                      cleanMetadata = {
                        ...baseMetadata, // Include required validation fields
                        subType: 'UPDATE_MEMBER_DATA',
                        type: '',
                        fieldValue: '',
                      };
                      break;
                    default:
                      cleanMetadata = {
                        ...baseMetadata,
                        subType: actionType,
                      };
                      break;
                  }

                  return cleanMetadata;
                };

                const newMetadata = getCleanMetadataForActionType(value);
                onUpdateNode(selectedNode.id, {
                  subType: value as AutomationActionNodeSubType,
                  metadata: newMetadata,
                });
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select action type" />
              </SelectTrigger>
              <SelectContent>
                {actionOptions.map((option) => (
                  <SelectItem key={option.type} value={option.type}>
                    {option.type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

        // Render specific action form based on subType
        if (selectedNode.subType === AutomationActionNodeSubType.SEND_EMAIL) {
          return (
            <div>
              {actionTypeSelector}
              <SendEmailActionForm
                selectedNode={selectedNode}
                onUpdateNode={onUpdateNode}
                actionOptions={actionOptions}
                onUpdateMessageTemplate={onUpdateMessageTemplate || (() => {})}
                resetMessageTemplate={resetMessageTemplate}
                onSave={onClose || (() => {})}
                onCancel={onClose || (() => {})}
              />
            </div>
          );
        }

        if (selectedNode.subType === AutomationActionNodeSubType.UPDATE_MEMBER_DATA) {
          return (
            <div>
              {actionTypeSelector}
              <UpdateMemberDataActionForm
                selectedNode={selectedNode}
                onUpdateNode={onUpdateNode}
                actionOptions={actionOptions}
                onSave={onClose || (() => {})}
                onCancel={onClose || (() => {})}
              />
            </div>
          );
        }

        // For other action types, show generic form
        return (
          <div>
            {actionTypeSelector}
            <GenericActionForm
              selectedNode={selectedNode}
              onUpdateNode={onUpdateNode}
              onSave={onClose || (() => {})}
              onCancel={onClose || (() => {})}
            />
          </div>
        );
      }

      default:
        return (
          <div className="text-sm text-grey-5">
            No specific properties for this node type
          </div>
        );
    }
  };

  return (
    <Card className="h-full rounded-none border-r-0 border-t-0 border-b-0 flex flex-col">
      <CardHeader className="pb-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Properties</CardTitle>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCopy}
              className="h-6 w-6 p-0"
            >
              <Copy className="h-3 w-3" />
            </Button>
            {canPaste && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handlePaste}
                className="h-6 w-6 p-0"
              >
                <Clipboard className="h-3 w-3" />
              </Button>
            )}
            {/* Hide delete button for root nodes since they cannot be deleted */}
            {!isRoot && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDelete}
                className="h-6 w-6 p-0 text-red-500 hover:text-red-600"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            )}
            {onClose && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="h-6 w-6 p-0"
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">
            {selectedNode.type}
          </Badge>
          {selectedNode.subType && (
            <Badge variant="outline" className="text-xs">
              {selectedNode.subType}
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col overflow-hidden p-4 relative">
        <div className="flex-1 min-h-0 overflow-hidden">
          {renderNodeSpecificProperties()}
        </div>

        <div className="pt-4 border-t border-grey-2 flex-shrink-0 mt-2">
          <div className="text-xs text-grey-5 space-y-1">
            <div>Children: {selectedNode.children.length}</div>
            {selectedNode.type === 'CONDITION' && (
              <div>Else Children: {selectedNode.elseChildren?.length || 0}</div>
            )}
            <div>Variables: {selectedNode.variables.length}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
